import React, { useEffect, useState } from 'react';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const STORAGE_KEY = 'mploi_interview_company_profile';

interface CompanyProfile {
  accountType: 'company' | 'candidate';
  // Company / Employer fields
  companyName: string;
  logoUrl: string;
  contactName: string;
  replyToEmail: string;
  website: string;
  senderName: string;
  brandColor: string;
  inviteMessage: string;
  // Candidate (self-practice) fields
  fullName: string;
  email: string;
  targetRole: string;
  experienceLevel: string;
  linkedinUrl: string;
  resumeFileName: string;
  resumeData: string; // base64 data URL of the uploaded resume
}

const defaultProfile: CompanyProfile = {
  accountType: 'company',
  companyName: '',
  logoUrl: '',
  contactName: '',
  replyToEmail: '',
  website: '',
  senderName: '',
  brandColor: '#0065FF',
  inviteMessage: 'We would like to invite you to complete an AI interview for this role. Please use the link below when you are ready.',
  fullName: '',
  email: '',
  targetRole: '',
  experienceLevel: '',
  linkedinUrl: '',
  resumeFileName: '',
  resumeData: '',
};

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  color: '#333',
  marginBottom: '6px',
};

const helpText: React.CSSProperties = {
  ...styles.secondaryText,
  fontSize: '12px',
  marginTop: '6px',
};

const InterviewCompanyProfile: React.FC = () => {
  const [profile, setProfile] = useState<CompanyProfile>(defaultProfile);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setProfile({ ...defaultProfile, ...JSON.parse(saved) });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  }, []);

  const updateField = (field: keyof CompanyProfile, value: string) => {
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setProfile((current) => ({ ...current, resumeFileName: file.name, resumeData: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = () => {
    if (profile.accountType === 'candidate') {
      if (!profile.fullName.trim()) return ValidationErrorToast('Required', 'Your name is required');
      if (!/^\S+@\S+\.\S+$/.test(profile.email.trim())) return ValidationErrorToast('Required', 'A valid email is required');
      if (!profile.targetRole.trim()) return ValidationErrorToast('Required', "Tell us the role or industry you're practising for");
      if (!profile.resumeFileName && !profile.linkedinUrl.trim()) {
        return ValidationErrorToast('Required', 'Upload a resume, or add a LinkedIn URL, so Jenny can tailor your interview');
      }
    } else {
      if (!profile.companyName.trim()) return ValidationErrorToast('Required', 'Company name is required for company profiles');
    }

    const toSave = { ...profile, updatedAt: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (err) {
      // Resume data may exceed the storage quota — persist everything except the file bytes.
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...toSave, resumeData: '' }));
      } catch (e2) {
        console.error('Could not persist profile', e2);
      }
    }
    SuccessToast('Saved', profile.accountType === 'candidate' ? 'Candidate profile saved' : 'Interview profile and branding details saved');
  };

  const resetProfile = () => {
    if (!window.confirm('Reset the saved profile details?')) return;
    localStorage.removeItem(STORAGE_KEY);
    setProfile({ ...defaultProfile, accountType: profile.accountType });
    SuccessToast('Reset', 'Profile has been reset');
  };

  const isCandidate = profile.accountType === 'candidate';
  const displayCompany = profile.companyName || 'Your Company';
  const displaySender = profile.senderName || profile.contactName || displayCompany;

  return (
    <InterviewSidebar>
      <div className="row">
        <div className="col-lg-7" style={{ marginBottom: '24px' }}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>{isCandidate ? 'Candidate Profile' : 'Business Profile & Branding'}</h3>
            <p style={{ ...styles.secondaryText, marginTop: '-8px', marginBottom: '20px' }}>
              {isCandidate
                ? 'Tell us about yourself so Jenny can tailor your practice interview and feedback.'
                : 'Set who is using the interview simulator and how candidate invitations should be presented.'}
            </p>

            {/* Profile type toggle */}
            <div style={{ marginBottom: '18px' }}>
              <label style={fieldLabel}>Profile type</label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {(['company', 'candidate'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => updateField('accountType', type)}
                    style={{
                      ...styles.button,
                      ...(profile.accountType === type ? styles.primaryButton : styles.secondaryButton),
                      textTransform: 'capitalize',
                    } as any}
                  >
                    {type === 'company' ? 'Company / Employer' : 'Candidate'}
                  </button>
                ))}
              </div>
              <div style={helpText}>
                {isCandidate
                  ? "You're practising your own interview skills and getting feedback from Jenny."
                  : "You're an employer inviting candidates to interview for your roles."}
              </div>
            </div>

            {isCandidate ? (
              /* ---------- CANDIDATE FORM ---------- */
              <div className="row">
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Full name *</label>
                  <input style={styles.modalInput} value={profile.fullName} onChange={(e) => updateField('fullName', e.target.value)} placeholder="Your name" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Email *</label>
                  <input type="email" style={styles.modalInput} value={profile.email} onChange={(e) => updateField('email', e.target.value)} placeholder="you@email.com" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Role / industry you're practising for *</label>
                  <input style={styles.modalInput} value={profile.targetRole} onChange={(e) => updateField('targetRole', e.target.value)} placeholder="e.g. Sales Representative" />
                  <div style={helpText}>Jenny tailors her questions to this role.</div>
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Experience level</label>
                  <select style={styles.modalInput as any} value={profile.experienceLevel} onChange={(e) => updateField('experienceLevel', e.target.value)}>
                    <option value="">Select (optional)</option>
                    <option value="Student / Entry level">Student / Entry level</option>
                    <option value="Mid level">Mid level</option>
                    <option value="Senior">Senior</option>
                    <option value="Executive">Executive</option>
                  </select>
                </div>

                <div className="col-12" style={{ marginTop: '4px', marginBottom: '10px' }}>
                  <div style={{ padding: '14px 16px', borderRadius: '10px', background: '#F0F6FF', border: '1px solid #D6E4FF', fontSize: '13px', color: '#334' }}>
                    So Jenny understands your background, please add <strong>either a resume (preferred) or a LinkedIn URL</strong> — at least one is required.
                  </div>
                </div>

                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Resume (preferred)</label>
                  <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleResumeUpload} style={{ ...styles.modalInput, padding: '10px' } as any} />
                  {profile.resumeFileName && (
                    <div style={{ ...helpText, color: '#1e7d40' }}>✓ {profile.resumeFileName} uploaded</div>
                  )}
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>LinkedIn URL {profile.resumeFileName ? '' : '(if no resume)'}</label>
                  <input type="url" style={styles.modalInput} value={profile.linkedinUrl} onChange={(e) => updateField('linkedinUrl', e.target.value)} placeholder="https://www.linkedin.com/in/you" />
                  <div style={helpText}>Jenny can read this instead of a resume.</div>
                </div>
              </div>
            ) : (
              /* ---------- COMPANY / EMPLOYER FORM (unchanged) ---------- */
              <div className="row">
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Company name</label>
                  <input style={styles.modalInput} value={profile.companyName} onChange={(e) => updateField('companyName', e.target.value)} placeholder="Acme Pty Ltd" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Logo URL</label>
                  <input style={styles.modalInput} value={profile.logoUrl} onChange={(e) => updateField('logoUrl', e.target.value)} placeholder="https://example.com/logo.png" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Contact name</label>
                  <input style={styles.modalInput} value={profile.contactName} onChange={(e) => updateField('contactName', e.target.value)} placeholder="Hiring manager name" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Reply-to email</label>
                  <input type="email" style={styles.modalInput} value={profile.replyToEmail} onChange={(e) => updateField('replyToEmail', e.target.value)} placeholder="talent@company.com" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Website</label>
                  <input type="url" style={styles.modalInput} value={profile.website} onChange={(e) => updateField('website', e.target.value)} placeholder="https://company.com" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Invite sender name</label>
                  <input style={styles.modalInput} value={profile.senderName} onChange={(e) => updateField('senderName', e.target.value)} placeholder="Acme Talent Team" />
                </div>
                <div className="col-md-6" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Brand colour</label>
                  <input type="color" style={{ ...styles.modalInput, height: '48px', padding: '8px' } as any} value={profile.brandColor} onChange={(e) => updateField('brandColor', e.target.value)} />
                </div>
                <div className="col-12" style={{ marginBottom: '14px' }}>
                  <label style={fieldLabel}>Default invite message</label>
                  <textarea style={{ ...styles.modalInput, minHeight: '130px' } as any} value={profile.inviteMessage} onChange={(e) => updateField('inviteMessage', e.target.value)} />
                </div>
              </div>
            )}

            <div style={styles.modalFooter}>
              <button type="button" onClick={resetProfile} style={{ ...styles.button, ...styles.secondaryButton } as any}>Reset</button>
              <button type="button" onClick={saveProfile} style={{ ...styles.button, ...styles.primaryButton } as any}>Save Profile</button>
            </div>
          </div>
        </div>

        <div className="col-lg-5" style={{ marginBottom: '24px' }}>
          {isCandidate ? (
            /* ---------- CANDIDATE PREVIEW ---------- */
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Your Practice Profile</h3>
              <div style={{ border: '1px solid #E0E0E0', borderRadius: '14px', padding: '24px', background: '#fff' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#0065FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '20px', marginBottom: '16px' }}>
                  {(profile.fullName || 'You').slice(0, 2).toUpperCase()}
                </div>
                <h4 style={{ ...styles.primaryText, marginBottom: '6px' }}>Ready to practise, {profile.fullName || 'there'}!</h4>
                <p style={{ ...styles.secondaryText, lineHeight: '1.7' }}>
                  {profile.targetRole
                    ? <>Jenny will run a mock interview for <strong>{profile.targetRole}</strong>.</>
                    : 'Add a target role and Jenny will tailor your questions.'}
                </p>
                <div style={{ marginTop: '16px', padding: '14px', borderRadius: '10px', background: '#F8F9FA', fontSize: '13px', color: '#555' }}>
                  Jenny will read your{' '}
                  <strong>
                    {profile.resumeFileName ? profile.resumeFileName : (profile.linkedinUrl ? 'LinkedIn profile' : 'resume / LinkedIn (add one)')}
                  </strong>{' '}to personalise your questions.
                </div>
                <button style={{ ...styles.button, backgroundColor: '#0065FF', color: '#fff', marginTop: '18px', width: '100%' } as any}>Start a practice interview</button>
                {profile.email && (
                  <div style={{ ...styles.secondaryText, fontSize: '12px', marginTop: '12px', textAlign: 'center' }}>
                    Your recording &amp; Jenny's feedback will be saved to your account and sent to {profile.email}.
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ---------- COMPANY INVITE PREVIEW (unchanged) ---------- */
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Candidate Invite Preview</h3>
              <div style={{ border: '1px solid #E0E0E0', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
                <div style={{ background: profile.brandColor || '#0065FF', minHeight: '8px' }} />
                <div style={{ padding: '24px' }}>
                  {profile.logoUrl ? (
                    <img src={profile.logoUrl} alt={displayCompany} style={{ maxWidth: '160px', maxHeight: '70px', objectFit: 'contain', marginBottom: '18px' }} />
                  ) : (
                    <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: profile.brandColor || '#0065FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '20px', marginBottom: '18px' }}>
                      {displayCompany.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <h4 style={{ ...styles.primaryText, marginBottom: '8px' }}>Interview invitation from {displayCompany}</h4>
                  <p style={{ ...styles.secondaryText, lineHeight: '1.7' }}>{profile.inviteMessage}</p>
                  <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', background: '#F8F9FA' }}>
                    <div style={{ fontSize: '13px', color: '#555' }}>From</div>
                    <strong style={styles.primaryText}>{displaySender}</strong>
                    {profile.replyToEmail && <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>Reply-to: {profile.replyToEmail}</div>}
                  </div>
                  <button style={{ ...styles.button, backgroundColor: profile.brandColor || '#0065FF', color: '#fff', marginTop: '18px', width: '100%' } as any}>Start Interview</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </InterviewSidebar>
  );
};

export default InterviewCompanyProfile;
